import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { StaffCombobox } from "@/components/StaffCombobox";
import { toast } from "sonner";

const BLANK = { title: "", notes: "", due: "", assignee_email: "", assignee_name: "", matter_id: "", repeat: "" };
const NONE = "__none__";
export const REPEAT_LABEL = { "": "Does not repeat", weekly: "Weekly", biweekly: "Every 2 weeks", monthly: "Monthly" };

export function TaskDialog({ open, setOpen, task, preset, onSaved }) {
  const { user } = useAuth();
  const canAssign = ["admin", "manager"].includes(user?.role);
  const [form, setForm] = useState(BLANK);
  const [matters, setMatters] = useState([]);
  useEffect(() => {
    if (!open) return;
    setForm(task
      ? { title: task.title, notes: task.notes || "", due: task.due || "", assignee_email: task.assignee_email, assignee_name: task.assignee_name, matter_id: task.matter_id || "", repeat: task.repeat || "" }
      : { ...BLANK, ...(preset || {}) });
    api.get("/matters", { params: { status: "open" } }).then((r) => setMatters(r.data)).catch((e) => console.error("matters", e));
  }, [open, task, preset]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the task a title");
    const payload = { ...form, matter_id: form.matter_id === NONE ? "" : form.matter_id, repeat: form.repeat === NONE ? "" : form.repeat };
    try {
      if (task) await api.patch(`/tasks/${task.id}`, payload); else await api.post("/tasks", payload);
      toast.success(task ? "Task updated" : (payload.assignee_email && payload.assignee_email !== user.email ? `Task assigned to ${payload.assignee_name}` : "Task added"));
      setOpen(false); onSaved?.();
    } catch (e) { console.error("save task", e); toast.error(e?.response?.data?.detail || "Could not save task"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle>{task ? "Edit task" : "New task"}</DialogTitle>
          <DialogDescription>{canAssign ? "Assign to yourself or anyone on the team." : "Tasks you create are assigned to you."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title *</Label><Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Draft demand letter" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Due date</Label><Input type="date" data-testid="task-due" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} /></div>
            <div><Label className="text-xs">Assignee</Label>
              {canAssign
                ? <><StaffCombobox testid="task-assignee" value={form.assignee_name} placeholder="Me (default)" onChange={(name, email) => setForm({ ...form, assignee_name: name, assignee_email: email })} /><span className="block text-[11px] text-muted-foreground mt-1">Leave blank to keep it yourself.</span></>
                : <Input value={user?.name || user?.email || ""} disabled data-testid="task-assignee-self" />}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Related matter (optional)</Label>
              <Select value={form.matter_id || NONE} onValueChange={(v) => setForm({ ...form, matter_id: v })}>
                <SelectTrigger data-testid="task-matter"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value={NONE}>None</SelectItem>
                  {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.matter_number || m.client_name} · {m.client_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Repeat</Label>
              <Select value={form.repeat || NONE} onValueChange={(v) => setForm({ ...form, repeat: v })}>
                <SelectTrigger data-testid="task-repeat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(REPEAT_LABEL).map(([k, l]) => <SelectItem key={k || NONE} value={k || NONE}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.repeat && form.repeat !== NONE && <span className="block text-[11px] text-muted-foreground mt-1">When completed, the next one is scheduled automatically.</span>}
            </div>
          </div>
          <div><Label className="text-xs">Notes</Label><Textarea rows={3} data-testid="task-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="task-save" onClick={save}>{task ? "Save changes" : "Add task"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
